import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { OrdersAPI } from '../api';
import { ExternalServicesAPI } from '../api/external-services';
import { UserProfile } from './auth';
import { PaymentModal } from './PaymentModal';
import { ExternalServiceModal } from './ExternalServiceModal';
import { CpanelAccessModal } from './CpanelAccessModal';
import { RichEditor } from './RichEditor';
import { SafeHtml } from './SafeHtml';
import { Shield, Package, Server, Trash2, Wallet, Pencil, FileText, Plus, Globe, ChevronDown, ChevronUp } from 'lucide-react';

function ExpandableNote({ note }: { note: string | null }) {
  const [expanded, setExpanded] = useState(false);
  
  const hasContent = note && note.replace(/<[^>]*>?/gm, '').trim().length > 0;

  return (
    <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
      {hasContent ? (
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-start justify-between w-full text-left group"
        >
          <SafeHtml
            html={note}
            mode="rich"
            className={`text-sm text-gray-600 dark:text-gray-400 prose prose-sm dark:prose-invert max-w-none pr-2 flex-1 ${expanded ? '' : 'line-clamp-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0'}`}
          />
          <div className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-0.5">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
      ) : (
        <div className="h-5 w-full"></div>
      )}
    </div>
  );
}

export function Dashboard() {
  const { user, isLoaded } = useUser();
  const [orders, setOrders] = useState<any[]>([]);
  const [externalServices, setExternalServices] = useState<any[]>([]);
  const [isExternalModalOpen, setIsExternalModalOpen] = useState(false);
  const [editingExternalService, setEditingExternalService] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<any>(null);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [editDomainInput, setEditDomainInput] = useState('');
  const [editNotesInput, setEditNotesInput] = useState('');
  const [paymentOrder, setPaymentOrder] = useState<any>(null);
  const [cpanelOrder, setCpanelOrder] = useState<any>(null);

  useEffect(() => {
    async function fetchData() {
      if (!isLoaded || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [ordersResult, externalResult] = await Promise.all([
          OrdersAPI.listByUser(user.id),
          ExternalServicesAPI.listByUser(user.id)
        ]);

        if (ordersResult.success && ordersResult.data) {
          setOrders(ordersResult.data);
          setError(null);
        } else {
          setError(ordersResult.error || 'No se pudo cargar la información de las órdenes');
        }

        if (externalResult.success && externalResult.data) {
          setExternalServices(externalResult.data);
        }
      } catch {
        setError('Error al cargar los datos');
        console.error('Error fetching data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [user, isLoaded]);

  const handleCreateExternalService = async (data: any) => {
    if (!user) return;
    try {
      setLoading(true);
      const result = await ExternalServicesAPI.create(user.id, data);
      if (result.success) {
        const updatedList = await ExternalServicesAPI.listByUser(user.id);
        if (updatedList.success && updatedList.data) {
          setExternalServices(updatedList.data);
        }
      } else {
        alert('Error al crear servicio externo: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al crear el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExternalService = async (data: any) => {
    if (!user || !editingExternalService) return;
    try {
      setLoading(true);
      const result = await ExternalServicesAPI.update(editingExternalService.id, data);
      if (result.success) {
        const updatedList = await ExternalServicesAPI.listByUser(user.id);
        if (updatedList.success && updatedList.data) {
          setExternalServices(updatedList.data);
        }
      } else {
        alert('Error al actualizar servicio externo: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al actualizar el servicio');
    } finally {
      setLoading(false);
      setEditingExternalService(null);
    }
  };

  const handleDeleteExternalService = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este servicio externo?')) return;
    try {
      setLoading(true);
      const result = await ExternalServicesAPI.delete(id);
      if (result.success) {
        const updatedList = await ExternalServicesAPI.listByUser(user!.id);
        if (updatedList.success && updatedList.data) {
          setExternalServices(updatedList.data);
        }
      } else {
        alert('Error al eliminar servicio externo: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al eliminar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = (order: any) => {
    setOrderToDelete(order);
  };

  const handlePaymentClick = (order: any) => {
    setPaymentOrder(order);
  };

  const handleEditOrder = (order: any) => {
    setEditingOrder(order);
    setEditDomainInput(order.domain || '');
    setEditNotesInput(order.notes || '');
  };

  const confirmEditOrder = async () => {
    if (!editingOrder) return;

    try {
      setLoading(true);
      const result = await OrdersAPI.update(editingOrder.id, {
        domain: editDomainInput,
        notes: editNotesInput || null
      });

      if (result.success) {
        // Refresh list
        if (user) {
          const updatedList = await OrdersAPI.listByUser(user.id);
          if (updatedList.success && updatedList.data) {
            setOrders(updatedList.data);
          }
        }
      } else {
        alert('Error al actualizar: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al procesar la solicitud');
    } finally {
      setLoading(false);
      setEditingOrder(null);
    }
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      setLoading(true);

      const result = await OrdersAPI.delete(orderToDelete.id);

      if (result.success) {
        const svc = orderToDelete.hosting || orderToDelete.complement;
        await OrdersAPI.sendCancelEmail({
          userEmail: user?.primaryEmailAddress?.emailAddress || '',
          userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          planName: svc?.name || svc?.displayName,
          planPrice: svc?.price,
          planCurrency: svc?.currency,
          planPeriod: svc?.payType
        });

        // Refresh list
        if (user) {
          const updatedList = await OrdersAPI.listByUser(user.id);
          if (updatedList.success && updatedList.data) {
            setOrders(updatedList.data);
          }
        }
      } else {
        alert('Error al eliminar el servicio: ' + result.error);
      }
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al procesar la solicitud');
    } finally {
      setLoading(false);
      setOrderToDelete(null);
    }
  };

  const getNextPaymentDate = (createdAt: string, payType: string) => {
    if (!createdAt || !payType) return null;

    const date = new Date(createdAt);
    const now = new Date();

    // Avanzar la fecha de cobro hasta encontrar el próximo vencimiento a partir de hoy,
    // usando la misma lógica que el cron de expiraciones.
    while (date <= now) {
      if (payType === 'MENSUAL') {
        date.setMonth(date.getMonth() + 1);
      } else if (payType === 'ANUAL') {
        date.setFullYear(date.getFullYear() + 1);
      } else {
        return null;
      }
    }

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Acceso Requerido</h2>
          <p className="text-gray-600 dark:text-gray-400">Por favor, inicia sesión para acceder al dashboard.</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'PAID': return 'bg-green-100 text-green-800'
      case 'ACTIVE': return 'bg-blue-100 text-blue-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendiente de Pago'
      case 'PAID': return 'Pagado'
      case 'ACTIVE': return 'Activo'
      case 'CANCELLED': return 'Cancelado'
      default: return status
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 pb-20 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mis Servicios</h1>
              <p className="text-gray-600 mt-1 dark:text-gray-300">
                Bienvenido de vuelta, {user.primaryEmailAddress?.emailAddress || user.firstName}
              </p>
            </div>
            <UserProfile showDetails={true} />
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Mis Servicios Contratados</h2>
          <button
            onClick={() => setIsExternalModalOpen(true)}
            className="w-full sm:w-auto justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nuevo servicio Externo
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center dark:bg-slate-800 dark:border-slate-700">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4 dark:text-gray-600" />
            <h3 className="text-xl font-medium text-gray-900 mb-2 dark:text-white">No tienes servicios activos</h3>
            <p className="text-gray-500 mb-6 dark:text-gray-400">Aún no has contratado ningún servicio.</p>
            <a href="/" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700">
              Ver Planes
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.map((order) => {
              const svc = order.hosting || order.complement;
              const svcName = svc?.name || svc?.displayName || 'Servicio';
              const svcImage = svc?.mainPhotoUrl;
              const svcCurrency = svc?.currency;
              const quantity = order.quantity || 1;
              const svcPrice = svc ? (svc.price * quantity).toFixed(2) : '0.00';
              const svcPayType = svc?.payType || svc?.payTypeDisplay;
              // const isComplement = !!order.complement;

              return (
                <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700 flex flex-col h-full">
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden dark:bg-slate-700">
                        {svcImage ? (
                          <img
                            src={svcImage}
                            alt={svcName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Server className="h-6 w-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 dark:text-white">
                        {svcName} {quantity > 1 ? `(x${quantity})` : ''}
                      </h3>
                      {order.notes && (
                        <span title="Esta orden tiene notas">
                          <FileText className="w-4 h-4 text-blue-400 mb-1 flex-shrink-0" />
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{svcPayType}</p>

                    <div className="border-t border-gray-100 pt-4 space-y-3 dark:border-slate-700 mt-auto">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Dominio</span>
                        <span className={`font-medium ${order.domain ? 'text-gray-900 dark:text-white' : 'text-gray-400 italic dark:text-gray-500'}`}>
                          {order.domain || 'dominio.com'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Precio</span>
                        <span className="font-medium text-gray-900 dark:text-white">{svcCurrency} {svcPrice}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Fecha de Orden</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatDate(order.createdAt)}</span>
                      </div>
                      {getNextPaymentDate(order.createdAt, svcPayType) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Próximo Pago</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getNextPaymentDate(order.createdAt, svcPayType)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Ultimo medio de pago</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {order.lastPaymentMethod ? (
                            order.lastPaymentMethod === 'TRANSFERENCIA' ? 'Transferencia' : 
                            order.lastPaymentMethod.charAt(0) + order.lastPaymentMethod.slice(1).toLowerCase()
                          ) : 'No definido'}
                        </span>
                      </div>
                      
                      <ExpandableNote note={order.notes} />
                    </div>
                  </div>

                  <div className="flex border-t border-gray-200 divide-x divide-gray-200 bg-white dark:bg-slate-800 dark:border-slate-700 dark:divide-slate-700 mt-auto">
                    <button
                      className={`flex-1 flex items-center justify-center py-4 transition-all ${order.status === 'PENDING'
                        ? 'text-orange-600 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/40'
                        : 'text-gray-400 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-slate-700 dark:hover:text-blue-400'
                        }`}
                      title={order.status === 'PENDING' ? "Pagar (Pendiente)" : "Pagar"}
                      onClick={() => handlePaymentClick(order)}
                    >
                      <Wallet className="h-5 w-5" />
                    </button>
                    {(order.cpanelUser || order.cpanelPasswordEnc) && (
                      <button
                        className="flex-1 flex items-center justify-center py-4 text-gray-400 hover:text-blue-600 hover:bg-gray-50 transition-all dark:hover:bg-slate-700 dark:hover:text-blue-400"
                        title="Accesos cPanel"
                        onClick={() => setCpanelOrder(order)}
                      >
                        <div className="font-bold text-lg leading-none tracking-tighter" style={{ fontFamily: 'sans-serif' }}>
                          cP
                        </div>
                      </button>
                    )}
                    <button
                      className="flex-1 flex items-center justify-center py-4 text-gray-400 hover:text-blue-600 hover:bg-gray-50 transition-all dark:hover:bg-slate-700 dark:hover:text-blue-400"
                      title="Editar Datos"
                      onClick={() => handleEditOrder(order)}
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center py-4 text-gray-400 hover:text-red-600 hover:bg-gray-50 transition-all dark:hover:bg-slate-700 dark:hover:text-red-400"
                      title="Eliminar Servicio"
                      onClick={() => handleDeleteOrder(order)}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* External Services Section */}
        {externalServices.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 dark:text-white">Mis Servicios Externos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {externalServices.map((service) => (
                <div key={service.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700 flex flex-col h-full">
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden dark:bg-slate-700">
                        <Globe className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Externo
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 dark:text-white">
                        {service.name}
                      </h3>
                    </div>
                    {service.type && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 font-medium">{service.type}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{service.payType === 'ANUAL' ? 'Anual' : 'Mensual'}</p>

                    <div className="border-t border-gray-100 pt-4 space-y-3 dark:border-slate-700 mt-auto">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Dominio</span>
                        <span className={`font-medium ${service.domain ? 'text-gray-900 dark:text-white' : 'text-gray-400 italic dark:text-gray-500'}`}>
                          {service.domain || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Precio</span>
                        <span className="font-medium text-gray-900 dark:text-white">USD {service.price.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Inicio</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatDate(service.startDate)}</span>
                      </div>
                      {service.nextPaymentDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Próximo Pago</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatDate(service.nextPaymentDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex border-t border-gray-200 divide-x divide-gray-200 bg-white dark:bg-slate-800 dark:border-slate-700 dark:divide-slate-700 mt-auto">
                    <button
                      className="flex-1 flex items-center justify-center py-4 text-gray-400 hover:text-blue-600 hover:bg-gray-50 transition-all dark:hover:bg-slate-700 dark:hover:text-blue-400"
                      title="Editar Servicio"
                      onClick={() => setEditingExternalService(service)}
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center py-4 text-gray-400 hover:text-red-600 hover:bg-gray-50 transition-all dark:hover:bg-slate-700 dark:hover:text-red-400"
                      title="Eliminar Servicio"
                      onClick={() => handleDeleteExternalService(service.id)}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <ExternalServiceModal
          isOpen={isExternalModalOpen}
          onClose={() => setIsExternalModalOpen(false)}
          onConfirm={handleCreateExternalService}
        />

        {editingExternalService && (
          <ExternalServiceModal
            isOpen={!!editingExternalService}
            onClose={() => setEditingExternalService(null)}
            onConfirm={handleUpdateExternalService}
            initialData={{
              name: editingExternalService.name,
              type: editingExternalService.type,
              domain: editingExternalService.domain,
              price: editingExternalService.price,
              payType: editingExternalService.payType,
              startDate: editingExternalService.startDate,
              nextPaymentDate: editingExternalService.nextPaymentDate
            }}
          />
        )}

        {/* Edit Order Modal (client) */}
        {editingOrder && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setEditingOrder(null)}
          >
            <div
              className="bg-white text-gray-900 rounded-lg p-6 max-w-lg w-full shadow-xl dark:bg-slate-800 dark:text-white dark:border dark:border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 dark:text-white">Editar Datos</h3>

              <div className="mb-4">
                <label htmlFor="edit-domain" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Dominio
                </label>
                <input
                  type="text"
                  id="edit-domain"
                  placeholder="ejemplo.com"
                  value={editDomainInput}
                  onChange={(e) => setEditDomainInput(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Notas
                </label>
                <RichEditor
                  value={editNotesInput}
                  onChange={setEditNotesInput}
                  placeholder="Agregá notas, información adicional, etc."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={confirmEditOrder}
                  className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-medium transition-colors"
                >
                  Guardar
                </button>
                <button
                  onClick={() => setEditingOrder(null)}
                  className="px-6 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-full font-medium transition-colors dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {orderToDelete && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setOrderToDelete(null)}
          >
            <div
              className="bg-white text-gray-900 rounded-lg p-6 max-w-md w-full shadow-xl dark:bg-slate-800 dark:text-white dark:border dark:border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-2 dark:text-white">escalable.host dice</h3>
              <p className="text-gray-600 mb-6 dark:text-gray-300">
                ¿Estás seguro de que deseas eliminar el servicio {orderToDelete.hosting?.name || orderToDelete.complement?.displayName}? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={confirmDeleteOrder}
                  className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-medium transition-colors"
                >
                  Aceptar
                </button>
                <button
                  onClick={() => setOrderToDelete(null)}
                  className="px-6 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-full font-medium transition-colors dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={!!paymentOrder}
          onClose={() => setPaymentOrder(null)}
          paypalHtml={paymentOrder?.hosting?.paypalButtonHtml || paymentOrder?.complement?.paypalButtonHtml}
          productName={
            paymentOrder?.hosting?.name ||
            (paymentOrder?.complement?.displayName ? (paymentOrder.complement.displayName + ((paymentOrder.quantity || 1) > 1 ? ` (x${paymentOrder.quantity})` : '')) : 'Servicio')
          }
          productImage={paymentOrder?.hosting?.mainPhotoUrl || paymentOrder?.complement?.mainPhotoUrl}
          productPrice={
            paymentOrder ?
              ((paymentOrder.hosting?.price || paymentOrder.complement?.price || 0) * (paymentOrder.quantity || 1)).toFixed(2)
              : '0.00'
          }
          productCurrency={paymentOrder?.hosting?.currency || paymentOrder?.complement?.currency}
          payType={paymentOrder?.hosting?.payType || paymentOrder?.complement?.payType}
          quantity={paymentOrder?.quantity || 1}
        />

        {/* cPanel Access Modal */}
        <CpanelAccessModal
          open={!!cpanelOrder}
          onClose={() => setCpanelOrder(null)}
          order={cpanelOrder}
        />
      </div>
    </div>
  );
}

export default Dashboard;